import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPen, faCalendar, faDollarSign, faFileInvoice, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { fetchDespesaById } from '../../../services/despesaService';
import { formatBRL } from '../../../utils/currency';

const formatDateLocal = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const datePart = value.split('T')[0].split(' ')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10)).toLocaleDateString('pt-BR');
    }
  }
  try {
    return new Date(value).toLocaleDateString('pt-BR');
  } catch (e) {
    return String(value);
  }
};

const renderConta = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return value.descricao || value.label || value.nome || '';
  return String(value);
};

const DespesaView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [despesa, setDespesa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDespesa = async () => {
      try {
        setLoading(true);
        const data = await fetchDespesaById(id);
        setDespesa(data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Erro ao carregar despesa');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadDespesa();
    }
  }, [id]);

  const handleBack = () => {
    navigate('/financeiro/despesas');
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !despesa) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'Despesa não encontrada'}
        </div>
        <button
          onClick={handleBack}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
        >
          Voltar à Lista
        </button>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
          </button>
          <h2 className="text-xl font-bold">Detalhes da Despesa #{despesa.id}</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações Principais */}
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Informações Principais</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faFileInvoice} className="text-blue-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Descrição:</span>
                  <p className="text-gray-800">{despesa.descricao}</p>
                </div>
              </div>

              {despesa.numeroNF && (
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faFileInvoice} className="text-green-500 mt-1 mr-3" />
                  <div>
                    <span className="text-sm font-medium text-gray-600">Número NF:</span>
                    <p className="text-gray-800 font-mono">{despesa.numeroNF}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start">
                <FontAwesomeIcon icon={faDollarSign} className="text-red-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Valor:</span>
                  <p className="text-xl font-bold text-red-600">
                    {formatBRL(despesa.valor)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <FontAwesomeIcon icon={faBuilding} className="text-purple-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Conta:</span>
                  <p className="text-gray-800">{despesa.contaDescricao || renderConta(despesa.conta)}</p>
                </div>
              </div>

              <div className="flex items-start">
                <FontAwesomeIcon icon={faCalendar} className="text-blue-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Data do Balanço:</span>
                  <p className="text-gray-800">{formatDateLocal(despesa.dataBalanco)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Informações de Auditoria */}
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Informações de Auditoria</h3>
            
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-600">Cadastrado por:</span>
                <p className="text-gray-800">{despesa.cadastradoPor}</p>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-600">Data de cadastro:</span>
                <p className="text-gray-800">{formatDateLocal(despesa.dataInclusao)} {despesa.dataInclusao && new Date(despesa.dataInclusao).toLocaleTimeString('pt-BR')}</p>
              </div>

              {despesa.dataInclusao && (
                <div>
                  <span className="text-sm font-medium text-gray-600">Data de inclusão:</span>
                    <p className="text-gray-800">{formatDateLocal(despesa.dataInclusao)}</p>
                </div>
              )}

              {despesa.alteradoPor && (
                <>
                  <hr className="my-3" />
                  <div>
                    <span className="text-sm font-medium text-gray-600">Última alteração por:</span>
                    <p className="text-gray-800">{despesa.alteradoPor}</p>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-gray-600">Data da última alteração:</span>
                      <p className="text-gray-800">{formatDateLocal(despesa.alteradoEm)} {despesa.alteradoEm && new Date(despesa.alteradoEm).toLocaleTimeString('pt-BR')}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Card de Resumo */}
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-2 text-red-800">Resumo da Despesa</h3>
            <div className="text-sm text-red-700">
              <p className="mb-1">
                <span className="font-medium">Total:</span> {formatBRL(despesa.valor)}
              </p>
                <p className="mb-1">
                <span className="font-medium">Categoria:</span> {despesa.contaDescricao || despesa.conta}
              </p>
              {despesa.numeroNF && (
                <p>
                  <span className="font-medium">Documento:</span> {despesa.numeroNF}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
        <div className="flex gap-2">
          <button
            onClick={handleBack}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
          >
            Voltar
          </button>
          <Link
            to={`/financeiro/despesas/${despesa.id}/edit`}
            className="px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPen} />
            Editar
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DespesaView;
